import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { isPublicRespondentPath } from '../constants/publicRespondentRoutes';
import { AuthCheckContext } from './authCheck';
import { apiErrorMessage } from '../utils/apiErrorMessage';
import { postJsonWithChallenge } from '../utils/challengeFetch';
import { retryAfterSeconds } from '../utils/otpGuardResponse';

interface User {
  id: number;
  email: string;
  is_admin: boolean;
  policies_accepted_at: string | null;
  policies_version: string | null;
  access_blocked?: boolean;
  account_type?: string | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  policiesAccepted: boolean;
  isInternalUser: boolean;
  accountBlocked: boolean;
  login: (
    email: string,
    password: string,
  ) => Promise<{
    success: boolean;
    message: string;
    requiresVerification?: boolean;
    accountDisabled?: boolean;
    redirect?: string;
  }>;
  signup: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  verifyOtp: (
    email: string,
    otpCode: string,
  ) => Promise<{ success: boolean; message: string; accountDisabled?: boolean; redirect?: string }>;
  loginWithCode: (
    email: string,
    code: string,
  ) => Promise<{ success: boolean; message: string; accountDisabled?: boolean; redirect?: string }>;
  requestSignInLink: (
    email: string,
  ) => Promise<{ sent: boolean; signedIn: boolean; message: string; retryAfter?: number }>;
  acceptPolicies: () => Promise<{ success: boolean; message: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// FastAPI error bodies put the payload in `detail`, which may be a plain string
// OR a structured object ({code, message, redirect}). Always coerce to a string
// before it reaches React state — rendering a raw object throws
// "Objects are not valid as a React child" and trips the ErrorBoundary.
//
// One line over the shared serialiser (src/utils/apiErrorMessage.ts). This was one of four
// hand-rolled copies of the same coercion; the quota refusal that reached a summit attendee as
// "[object Object]" matched none of them, which is the argument for a single implementation.
const detailMessage = (data: { error?: unknown; detail?: unknown }, fallback: string): string =>
  apiErrorMessage(data, fallback);

// In Vite, environment variables are accessed via import.meta.env
// and need to be prefixed with VITE_ instead of NEXT_PUBLIC_
const API_URL = import.meta.env.VITE_API_URL || '';
const POLICY_VERSION = import.meta.env.VITE_POLICY_VERSION || '1.0';
const POLICY_ACCEPTANCE_DISABLED = import.meta.env.VITE_POLICY_ACCEPTANCE_DISABLED === 'true';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // ADR-0013
  const [startedOnPublicRoute] = useState(() => isPublicRespondentPath(window.location.pathname));
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(!startedOnPublicRoute);
  const [accountBlocked, setAccountBlocked] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [checkRequested, setCheckRequested] = useState(!startedOnPublicRoute);

  // Helper function to check if user has aetv.com email domain

  // Helper function to check if user has HBS email domain or is a specific HBS-access user

  // Malk (malkorganics.com) — email-domain driven, mirrors the other single-client flags.
  // Lowercase first so a mixed-case email domain still matches.

  // Internal BluePill team members (by email domain)
  const isInternalUser = user?.email?.endsWith('@blue-pill.ai') || false;

  // Check if user is from Smartypants domain or in specific access list
  const SMARTYPANTS_ACCESS_EMAILS: string[] = [];

  // Check if user is from KNF (Kettle & Fire) domain or in specific access list
  const KNF_ACCESS_EMAILS = ['knf@blue-pill.ai'];
  KNF_ACCESS_EMAILS.includes(user?.email || '');
  SMARTYPANTS_ACCESS_EMAILS.includes(user?.email || '');

  // Check if user is from Bacchus Worldwide domain

  // Check if user is from WellnessPet domain

  // Check if user is from Archerjerky domain

  // Check if user is from Amy's domain (Amy's × Albertsons combined survey chat)

  // Gaia Herbs (gaiaherbs.com) — email-domain driven, mirrors the other single-client
  // flags. Sees only the Concept Test use case, running on population_gaia_deep.

  // the Concept Test use case, running on population_gummy_candy.

  // Logout function (defined early so it can be used in checkAuthStatus)
  const logout = async () => {
    try {
      await fetch(`${API_URL}/logout`, {
        method: 'POST',
        credentials: 'include',
      });

      // Clear user from state and storage
      setUser(null);
      setAccountBlocked(false);
      localStorage.removeItem('user');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Check if user is already logged in by verifying cookie with /me endpoint
  useEffect(() => {
    if (!checkRequested) return;
    const checkAuthStatus = async () => {
      try {
        // Verify authentication by calling /me endpoint
        const response = await fetch(`${API_URL}/me`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        if (response.status === 401 || response.status === 403) {
          // Cookie is invalid or expired, clear user and redirect to login
          setUser(null);
          localStorage.removeItem('user');
          // The App component will handle redirecting to login based on isAuthenticated
        } else if (response.ok) {
          const data = await response.json();
          const userData: User = {
            id: data.id,
            email: data.email,
            is_admin: data.is_admin || false,
            policies_accepted_at: data.policies_accepted_at || null,
            policies_version: data.policies_version || null,
            access_blocked: data.access_blocked || false,
            account_type: data.account_type || null,
          };
          setUser(userData);
          setAccountBlocked(!!data.access_blocked);
          localStorage.setItem('user', JSON.stringify(userData));
        } else {
          // Unexpected error, clear user to be safe
          setUser(null);
          localStorage.removeItem('user');
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        // On error, clear user to be safe
        setUser(null);
        localStorage.removeItem('user');
      } finally {
        setIsLoading(false);
        setAuthChecked(true);
      }
    };

    checkAuthStatus();
  }, [checkRequested]);

  const ensureAuthChecked = useCallback(() => setCheckRequested(true), []);

  // Login function
  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        // Check if verification is required (unverified user trying to login)
        if (data.requires_verification) {
          return {
            success: false,
            message: data.message || 'Email verification required',
            requiresVerification: true,
            // A marketplace account verifies on the marketplace gate, not here.
            redirect: data.redirect || undefined,
          };
        }

        const userData: User = {
          id: data.user.id,
          email: email,
          is_admin: data.user.is_admin,
          policies_accepted_at: data.user.policies_accepted_at || null,
          policies_version: data.user.policies_version || null,
          account_type: data.user.account_type || null,
        };
        setUser(userData);
        setAccountBlocked(false);
        localStorage.setItem('user', JSON.stringify(userData));
        // A marketplace account logging in here is signed in successfully (the session
        // cookie is shared) but belongs on the marketplace — the backend says where.
        // The caller navigates; we don't, so it can keep control of the transition.
        return { success: true, message: 'Login successful', redirect: data.redirect || undefined };
      } else if (response.status === 403 && data.detail?.code === 'ACCOUNT_DISABLED') {
        // Free-tier access is disabled — show the "book a call" wall
        setAccountBlocked(true);
        return {
          success: false,
          message: data.detail.message || 'Account disabled',
          accountDisabled: true,
        };
      } else if (response.status === 403 && data.detail?.redirect) {
        // Marketplace (or any other) account that must go to a different app — redirect.
        const target = data.detail.redirect;
        window.location.href = target;
        return {
          success: false,
          message: detailMessage(data, 'Redirecting you…'),
          redirect: target,
        };
      } else if (response.status === 403 && data.action === 'verify_email') {
        // Handle OTP verification case (legacy)
        return { success: false, message: 'verification_required', requiresVerification: true };
      } else {
        return { success: false, message: detailMessage(data, 'Login failed') };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'An error occurred during login' };
    }
  };

  // Signup function
  const signup = async (email: string, password: string) => {
    try {
      const response = await postJsonWithChallenge(`${API_URL}/signup`, { email, password });

      const data = await response.json();

      if (response.ok) {
        const userData: User = {
          id: data.user?.id || data.user_id,
          email: email,
          is_admin: data.user?.is_admin || false,
          policies_accepted_at: data.user?.policies_accepted_at || null,
          policies_version: data.user?.policies_version || null,
          account_type: data.user?.account_type || null,
        };
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return { success: true, message: 'Registration successful' };
      } else if (response.status === 409 && data.detail?.redirect) {
        // This email belongs to the separate marketplace app — send them there.
        window.location.href = data.detail.redirect;
        return { success: false, message: detailMessage(data, 'Redirecting you…') };
      } else {
        return { success: false, message: detailMessage(data, 'Registration failed') };
      }
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, message: 'An error occurred during registration' };
    }
  };

  // OTP verification function
  /**
   * Sign in with a code emailed to the user. Spec 25 / increment 05c.
   *
   * Lives HERE rather than in Login.tsx, which called `/login-with-code` with a raw fetch. The server set
   * the session cookie, so the API considered the person signed in — but nothing set `user`, so the route
   * guard bounced them to /auth until a reload re-mounted this provider and `checkAuthStatus` read `/me`.
   * Puneet hit exactly that on DEV: "kep redirecting to auth page than i reloaded and finally got in".
   *
   * Deliberately a fourth door on the context alongside `login` and `verifyOtp`, not a `refreshUser()` the
   * component calls after its own fetch: the four steps that constitute "signed in" here — setUser,
   * clearing accountBlocked, writing localStorage, and only then returning — must happen together, and a
   * component that assembles them itself is how one of them gets forgotten. This one already was.
   *
   * The ACCOUNT_DISABLED branch is not boilerplate: it is the branch a summit attendee hits once
   * SUMMIT_TIER_DISABLED is on, and without it they would see a raw error instead of the "book a call" wall.
   */
  /**
   * Ask for a sign-in link — and adopt the session when the server has already opened one.
   *
   * On DEV the backend signs the account in during `/request-login-code` and sends no email at all, because
   * that database holds real brand employees' addresses. It answers `signedIn: true` with the user, having
   * already set the cookie.
   *
   * This lives on the context and is not a `refreshUser()` the page calls after its own fetch, for the reason
   * `loginWithCode` below documents: the four steps that constitute "signed in" here — setUser, clearing
   * accountBlocked, writing localStorage, and only then returning — must happen together.
   */
  const requestSignInLink = async (email: string) => {
    try {
      const response = await postJsonWithChallenge(`${API_URL}/request-login-code`, { email });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        return {
          sent: false,
          signedIn: false,
          message: apiErrorMessage(data, 'Could not send a link. Try again.'),
          retryAfter: retryAfterSeconds(data, response.headers),
        };
      }

      if (data.signedIn && data.user) {
        const userData: User = {
          id: data.user.id,
          email: data.user.email || email,
          is_admin: data.user.is_admin,
          policies_accepted_at: data.user.policies_accepted_at || null,
          policies_version: data.user.policies_version || null,
          account_type: data.user.account_type || null,
        };
        setUser(userData);
        setAccountBlocked(false);
        localStorage.setItem('user', JSON.stringify(userData));
        return { sent: false, signedIn: true, message: data.message || 'Signed in' };
      }

      return { sent: true, signedIn: false, message: data.message || 'Check your email.' };
    } catch {
      return {
        sent: false,
        signedIn: false,
        message: 'Could not reach the server. Check your connection and try again.',
      };
    }
  };

  const loginWithCode = async (email: string, code: string) => {
    try {
      const response = await postJsonWithChallenge(`${API_URL}/login-with-code`, {
        email,
        code,
        policies_accepted: false,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const userData: User = {
          id: data.user.id,
          // The server echoes the address it authenticated; fall back to what was typed so a thin
          // response body cannot leave the app signed in as `undefined`.
          email: data.user.email || email,
          is_admin: data.user.is_admin,
          policies_accepted_at: data.user.policies_accepted_at || null,
          policies_version: data.user.policies_version || null,
          account_type: data.user.account_type || null,
        };
        setUser(userData);
        setAccountBlocked(false);
        localStorage.setItem('user', JSON.stringify(userData));
        return {
          success: true,
          message: data.message || 'Signed in successfully',
          redirect: data.redirect || undefined,
        };
      }

      if (response.status === 403 && data.detail?.code === 'ACCOUNT_DISABLED') {
        setAccountBlocked(true);
        return {
          success: false,
          message: data.detail.message || 'Account disabled',
          accountDisabled: true,
        };
      }

      // FastAPI puts the reason in `detail`, as either a string or an object.
      const detail = data.detail;
      return {
        success: false,
        message:
          (typeof detail === 'string' ? detail : detail?.message) ||
          data.message ||
          'That code did not work.',
      };
    } catch (error) {
      console.error('Login with code failed:', error);
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  const verifyOtp = async (email: string, otpCode: string) => {
    try {
      const response = await postJsonWithChallenge(`${API_URL}/verify-otp`, {
        email,
        otp_code: otpCode,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const userData: User = {
          id: data.user.id,
          email: data.user.email,
          is_admin: data.user.is_admin,
          policies_accepted_at: data.user.policies_accepted_at || null,
          policies_version: data.user.policies_version || null,
          account_type: data.user.account_type || null,
        };
        setUser(userData);
        setAccountBlocked(false);
        localStorage.setItem('user', JSON.stringify(userData));
        return { success: true, message: 'Verification successful' };
      } else if (response.status === 403 && data.detail?.code === 'ACCOUNT_DISABLED') {
        // Free-tier access is disabled — show the "book a call" wall
        setAccountBlocked(true);
        return {
          success: false,
          message: data.detail.message || 'Account disabled',
          accountDisabled: true,
        };
      } else if (response.status === 403 && data.detail?.redirect) {
        // Marketplace (or any other) account that must go to a different app — redirect.
        const target = data.detail.redirect;
        window.location.href = target;
        return {
          success: false,
          message: detailMessage(data, 'Redirecting you…'),
          redirect: target,
        };
      } else {
        return {
          success: false,
          message: data.message || detailMessage(data, 'Verification failed'),
        };
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      return { success: false, message: 'An error occurred during verification' };
    }
  };

  const acceptPolicies = async () => {
    try {
      const response = await fetch(`${API_URL}/accept-policies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: POLICY_VERSION }),
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (user) {
          const updatedUser: User = {
            ...user,
            policies_accepted_at: data.policies_accepted_at,
            policies_version: POLICY_VERSION,
          };
          setUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
        return { success: true, message: 'Policies accepted' };
      }
      return { success: false, message: detailMessage(data, 'Failed to accept policies') };
    } catch (error) {
      console.error('Accept policies error:', error);
      return { success: false, message: 'An error occurred' };
    }
  };

  const policiesAccepted =
    POLICY_ACCEPTANCE_DISABLED ||
    (!!user?.policies_accepted_at && user?.policies_version === POLICY_VERSION);

  const authCheck = useMemo(
    () => ({ authChecked, ensureAuthChecked }),
    [authChecked, ensureAuthChecked],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isAdmin: user?.is_admin || false,
        policiesAccepted,
        isInternalUser,
        accountBlocked,
        login,
        signup,
        verifyOtp,
        loginWithCode,
        requestSignInLink,
        acceptPolicies,
        logout,
      }}
    >
      <AuthCheckContext.Provider value={authCheck}>
        {!isLoading && children}
      </AuthCheckContext.Provider>
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Auth state where it is optional — returns undefined instead of throwing when no
 * AuthProvider is mounted. For features that only *enhance* a view (e.g. an admin-only
 * overlay): they fail closed rather than taking the whole view down with them.
 * If your feature genuinely requires a signed-in user, use `useAuth`.
 */
export const useOptionalAuth = (): AuthContextType | undefined => useContext(AuthContext);
